import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import clsx from 'clsx';

import {
    KPI_VALUE_CLASSIC,
    KPI_VALUE_MEDIA_VIEWER_CLASSIC,
    KPI_VALUE_MEDIA_VIEWER_PERCENT,
    KPI_VALUE_PERCENT
} from '@/utils/hooks/format/useFormatKpiValue';
import useTranslation from '@/utils/hooks/translation/useTranslation';
// Hooks
import useAcl from '@/utils/hooks/user/useAcl';

import PropTypes from 'prop-types';

//lib
import { aclFeatures } from '@/lib/acl/aclFeatures';
import { ORIENTATION_VERTICAL } from '@/lib/gridElements/orientation';
import { YOUTUBE } from '@/lib/socialProfile';

import { DeprecatedKpiBadge } from '@/components/base/Badge/DeprecatedKpiBadge';
import { NewKpiBadge } from '@/components/base/Badge/NewKpiBadge';
//components
import KPI, { KPI_STYLES } from '@/components/base/Kpi';
import ErrorAnalyticsFallback from '@/components/layouts/ErrorFallbacks/ErrorAnalyticsFallback';

const getDataFormat = (format) => {
    switch (format) {
        case KPI_VALUE_PERCENT:
            return KPI_VALUE_MEDIA_VIEWER_PERCENT;
        case KPI_VALUE_CLASSIC:
            return KPI_VALUE_MEDIA_VIEWER_CLASSIC;
        default:
            return format;
    }
};

export const MediaViewerKPI = ({
    onReset = () => {
        /** */
    },
    data,
    isLoading,
    config,
    userNumbersFormat,
    children,
    renderLoader = () => {
        /** */
    },
    variant = 'noShadow',
    ...restProps
}) => {
    const { t } = useTranslation();
    const { hasAcl } = useAcl();

    return (
        <ErrorBoundary FallbackComponent={ErrorAnalyticsFallback} onReset={() => onReset()}>
            <KPI
                data={data}
                config={{
                    ...config,
                    data_format: getDataFormat(config?.data_format ?? KPI_VALUE_CLASSIC)
                }}
                userNumbersFormat={userNumbersFormat}
                isLoading={isLoading}
                kpiStyle={KPI_STYLES.MEDIA_VIEWER}
                renderLoader={renderLoader}
                variant={variant}
                {...restProps}>
                <DeprecatedKpiBadge
                    className="-top-2 left-2"
                    deprecated={config.deprecated}
                    network={config.social}
                />
                <NewKpiBadge config={config} className="-top-2 left-2" />
                {config?.forcedTooltip ? <KPI.KpiHelpTooltip /> : null}
                <KPI.Wrapper className="flex-col space-y-4 px-4 py-8">
                    <KPI.Icon className="h-[3.125rem] w-[3.125rem] text-xl" />
                    <KPI.Content
                        className={clsx({
                            'items-center': config.orientation === ORIENTATION_VERTICAL
                        })}>
                        <KPI.ContentMainDataContainer>
                            <KPI.MainDataWrapper>
                                <KPI.Promoted />
                                <KPI.MainData className="text-xl" />
                            </KPI.MainDataWrapper>
                            <KPI.KpiName className="text-center text-sm" />
                        </KPI.ContentMainDataContainer>
                        {hasAcl(aclFeatures.analytic.promoted_post.read) ? (
                            <KPI.SplitOrganicPayed />
                        ) : null}
                        {children}
                        {data?.data_estimated ? (
                            <p className="mt-2 text-center font-GMedium text-xs text-icoGray-400 dark:text-icoDarkMode-wolf">
                                {t(
                                    config?.network === YOUTUBE
                                        ? 'analytics:estimated_data_kpi_media_viewer_youtube'
                                        : 'analytics:estimated_data_kpi_media_viewer'
                                )}
                            </p>
                        ) : null}
                    </KPI.Content>
                </KPI.Wrapper>
            </KPI>
        </ErrorBoundary>
    );
};

MediaViewerKPI.propTypes = {
    onReset: PropTypes.func,
    data: PropTypes.object,
    config: PropTypes.object,
    userNumbersFormat: PropTypes.object,
    isLoading: PropTypes.bool,
    children: PropTypes.node,
    renderLoader: PropTypes.func
};
